<script lang="ts" setup>
import { compact, dayjs } from '@revolugo/common/utils'
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import { RAlert, RHotelRoomingListGuests, RPanel } from '#molecules'
import { HotelRoomingListGuestStatus, RAlertSeverity } from '#types'

import type { HotelRoomingListEditorProps } from './HotelRoomingListEditor.types.ts'
import type { HotelRoomingListGuestResponseDto } from '@revolugo/graphql/private'

const {
  locale,
  t,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
}: { locale: any; t: (key: string, params?: Record<string, any>) => string } =
  useI18n()

const props = defineProps<HotelRoomingListEditorProps>()

const status = ref<HotelRoomingListGuestStatus>()

const emits = defineEmits([
  'update-hotel-rooming-list-guest',
  'delete-hotel-rooming-list-guest',
])

const limitDate = computed(() => {
  const limitDates = compact(
    props.hotelRoomingList.map(hotelRoomingList => hotelRoomingList.limitDate),
  )

  if (!limitDates.length) {
    return undefined
  }

  const limitDateRaw = limitDates.reduce(
    (acc: string, hotelRoomingList: string) =>
      dayjs(acc).isBefore(hotelRoomingList) ? acc : hotelRoomingList,
  )

  return dayjs(limitDateRaw).locale(locale.value).format('LLL')
})

const isPassedLimitDate = computed(() => {
  if (!limitDate.value) {
    return false
  }

  const passedLimitDate = dayjs().isAfter(limitDate.value)
  if (passedLimitDate) {
    status.value = HotelRoomingListGuestStatus.LimitDatePassed
  }
  return passedLimitDate
})

const isFull = computed(() => {
  if (props.adultCount === null || props.adultCount === undefined) {
    return false
  }

  if (props.hotelRoomingList.length === 0) {
    return false
  }
  const guestsCount = props.hotelRoomingList.map(
    hotelRoomList => hotelRoomList.hotelRoomingListGuests.length,
  )
  if (!guestsCount) {
    return false
  }

  const totalGuests = guestsCount.reduce(
    (acc: number, guestsNumber: number) => acc + guestsNumber,
    0,
  )
  return props.adultCount - totalGuests <= 0
})

function saveHotelRoomingListGuest(
  hotelRoomingListGuest: HotelRoomingListGuestResponseDto,
) {
  if (!hotelRoomingListGuest.fullname) {
    return
  }

  status.value = HotelRoomingListGuestStatus.Loading
  emits('update-hotel-rooming-list-guest', hotelRoomingListGuest)
}

function deleteHotelRoomingListGuest({
  hotelRoomingListGuestId,
}: {
  hotelRoomingListGuestId: string
}) {
  status.value = HotelRoomingListGuestStatus.Loading
  emits('delete-hotel-rooming-list-guest', hotelRoomingListGuestId)
}

watch(
  () => props.error,
  () => {
    status.value = HotelRoomingListGuestStatus.Error
  },
)

watch(
  () => props.hotelRoomingList,
  () => {
    status.value = HotelRoomingListGuestStatus.Saved
  },
)

const hotelRoomingListGuestsStatus = computed(() => {
  switch (status.value) {
    case HotelRoomingListGuestStatus.Loading:
      return {
        icon: 'icon-[ph--info]',
        message: 'Loading',
        severity: RAlertSeverity.Info,
      }
    case HotelRoomingListGuestStatus.Error:
      return {
        icon: 'icon-[ph--warning]',
        message: isFull.value
          ? t('alert.list-full-error')
          : t('alert.internal-server-error', { email: 'care@revolugo.com' }),
        severity: RAlertSeverity.Error,
      }
    case HotelRoomingListGuestStatus.Saved:
      return {
        icon: 'icon-[ph--check-circle]',
        message: t('alert.saved'),
        severity: RAlertSeverity.Success,
      }
    case HotelRoomingListGuestStatus.Changed:
      return {
        icon: 'icon-[ph--warning-circle]',
        message: t('alert.changed'),
        severity: RAlertSeverity.Error,
      }
    case HotelRoomingListGuestStatus.LimitDatePassed:
      return {
        icon: 'icon-[ph--warning]',
        message: t('alert.limitDatePassed'),
        severity: RAlertSeverity.Warn,
      }
    case undefined:
    default:
      return {
        icon: 'icon-[ph--info]',
        message: '',
        severity: RAlertSeverity.Info,
      }
  }
})
</script>

<template>
  <div>
    <slot name="header">
      <h2
        class="text-lg font-sans font-semibold mb-1"
        data-testid="rooming-list-editor-title"
      >
        {{ t('title') }}<span v-if="limitDate && !isPassedLimitDate">*</span>
      </h2>
      <p
        class="text-sm font-sans mb-4"
        data-testid="rooming-list-editor-subtitle"
      >
        {{ t('subtitle') }}
      </p>
    </slot>
    <RAlert
      v-if="!!status"
      :message="hotelRoomingListGuestsStatus.message"
      :severity="hotelRoomingListGuestsStatus.severity"
      :icon="hotelRoomingListGuestsStatus.icon"
      data-testid="rooming-list-editor-alert"
      class="mb-4"
    />
    <RPanel
      v-for="(roomingList, index) in hotelRoomingList"
      class="mb-4"
      :key="index"
      :data-testid="`rooming-list-editor-panel-${index}`"
      :header="roomingList.hotelRoom.name"
      toggleable
    >
      <RHotelRoomingListGuests
        :hotelRoomingListGuests="roomingList.hotelRoomingListGuests"
        :occupancy="roomingList.hotelRoom.occupancy"
        :hotelRoomingListId="roomingList.id"
        :isFull="isFull"
        :isDisabled="isPassedLimitDate"
        @updateRoomingListGuest="saveHotelRoomingListGuest"
        @deleteRoomingListGuest="deleteHotelRoomingListGuest"
        data-testid="rooming-list-editor-rooming-list-guests"
        v-model:status="status"
      />
    </RPanel>
    <p
      v-if="limitDate && !isPassedLimitDate"
      class="text-sm font-sans"
      data-testid="rooming-list-editor-limit-date"
    >
      *{{ t('limitDate', { date: limitDate }) }}
    </p>
  </div>
</template>

<i18n lang="json" src="./HotelRoomingListEditor.i18n.json"></i18n>
